import { FC, useEffect, useState } from 'react';

import cx from 'classnames';
import pluralize from 'pluralize';
import { prop } from 'ramda';

import Icon from 'components/shared/Icon';
import RightFill from 'components/shared/Icon/icons/RightFill';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';

import DashboardSearchFilterButton from '../../Pages/Admin/ContentLibrary/List/SearchFilter';
import styles from './index.module.css';
import { ICollapseProps } from './types';

export const Collapse: FC<ICollapseProps> = ({
  children,
  className,
  defaultActive = -1,
  titleTextProps,
  showFilterDeleteButton = true,
  isSmallSizeFilterButton = false,
  ...rest
}) => {
  const [active, setActive] = useState<number>(defaultActive);
  const { isMobile } = useMediaBreakpoints();

  const headClasses = cx(styles.head, isMobile && styles.headMobile);
  const headRightClasses = cx(styles.headRight, isMobile && styles.headRightMobile);

  useEffect(() => {
    setActive(defaultActive);
  }, [defaultActive]);

  const handleToggle = (currentIndex: number, newIndex: number) => {
    if (currentIndex > -1 && currentIndex === newIndex) {
      return setActive(-1);
    }
    return setActive(newIndex);
  };

  return (
    <div className={className} {...rest}>
      {children.map((elem, index) => {
        if (!elem) {
          return null;
        }

        const isActive = index === active;
        const title = elem?.props?.title;
        const subTitle = elem?.props?.subTitle;
        const input = elem?.props?.input;
        const searchResultsCount = elem?.props?.searchResultsCount;
        const searchFilter = elem?.props?.searchFilter;
        const withSearchFilters = elem?.props?.withSearchFilters;
        const identity = elem?.props?.identity;

        return (
          <div key={index}>
            <div className={styles.headWrapper}>
              <div
                onClick={() => {
                  if (elem?.props?.onClick) elem.props.onClick();
                  return handleToggle(active, index);
                }}
                className={headClasses}
              >
                <div className={styles.title}>
                  {title && (
                    <div {...titleTextProps} className={cx(styles.titleText, titleTextProps?.className)}>
                      {title}
                    </div>
                  )}
                  {withSearchFilters && (isActive || searchFilter) && (
                    /*@ts-ignore*/
                    <DashboardSearchFilterButton
                      showDeleteButton={showFilterDeleteButton}
                      showText
                      filter={searchFilter}
                      tableName={title}
                      table={identity}
                      isRoundButton={isMobile}
                      isSmallSizeFilterButton={isMobile && isSmallSizeFilterButton}
                    />
                  )}
                </div>
                <div className={headRightClasses}>
                  {input && isActive && (
                    <div className={styles.input} onClick={(e) => e.stopPropagation()}>
                      {input}
                    </div>
                  )}
                  {searchResultsCount > 0 && (
                    <div className={styles.searchResults}>
                      <span className={styles.count}>({searchResultsCount})</span> Search{' '}
                      {pluralize('Result', searchResultsCount)}
                    </div>
                  )}
                  <Icon component={RightFill} className={cx(styles.icon, isActive && prop('iconRotate', styles))} />
                </div>
              </div>

              {subTitle && isActive && <div className={styles.subHead}>{subTitle}</div>}
            </div>

            {isActive && <div>{children[active]}</div>}
          </div>
        );
      })}
    </div>
  );
};

export const Panel = ({ children }: { children: JSX.Element }) => children;
